/**
 * Enquiry fields that can be mapped onto a WhatsApp template variable.
 *
 * Must stay in sync with ENQUIRY_VARS in vc_be/utils/whatsappTemplateVars.js —
 * the backend rejects any `variable` outside that allow-list, so an entry added
 * here without its counterpart there silently resolves to an empty string.
 *
 * `resolve` powers the client-side preview only; the values actually sent are
 * resolved server-side, per recipient, at send time. Keep each `resolve` a
 * faithful copy of the backend's — a preview that disagrees with the send is
 * worse than no preview.
 */

const adminName = (a) => {
  if (!a) return "";
  const full = `${a.firstName || ""} ${a.lastName || ""}`.trim();
  return full || a.fullName || a.name || (a.email ? a.email.split("@")[0] : "");
};

const firstName = (full) => String(full || "").trim().split(/\s+/)[0] || "";

/** Mirrors the backend's fmtDate: institute timezone, "13 August 2026". */
const fmtDate = (value, tz) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return d.toLocaleDateString("en-IN", {
      timeZone: tz || "Asia/Kolkata", year: "numeric", month: "long", day: "numeric",
    });
  } catch {
    return d.toLocaleDateString("en-IN");
  }
};

/** Course.fees is free text — money-format only a bare number. */
const fmtLooseMoney = (value, currency) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (!/^\d+(\.\d+)?$/.test(raw)) return raw;
  const symbol = (currency || "INR") === "INR" ? "₹" : currency;
  return `${symbol} ${Number(raw).toLocaleString("en-IN")}`;
};

/** Display order of the optgroups. */
export const ENQUIRY_VAR_GROUP_ORDER = [
  "Lead", "Course", "Branch", "Team", "Institute", "Date & time",
];

export const ENQUIRY_DYNAMIC_VARS = [
  /* Lead */
  { value: "enquiry_name", label: "Lead name", group: "Lead", resolve: (e) => e.fullName || "there" },
  { value: "enquiry_first_name", label: "Lead first name", group: "Lead", resolve: (e) => firstName(e.fullName) || "there" },
  { value: "mobile", label: "Mobile", group: "Lead", resolve: (e) => e.mobile || "" },
  { value: "email", label: "Email", group: "Lead", resolve: (e) => e.email || "" },
  { value: "enquiry_status", label: "Status", group: "Lead", resolve: (e) => e.status || "Enquired" },
  { value: "enquiry_source", label: "Lead source", group: "Lead", resolve: (e) => e.source || e.leadSource || "" },
  { value: "enquiry_date", label: "Enquiry date", group: "Lead", resolve: (e, ctx) => fmtDate(e.createdAt, ctx.timezone) },
  { value: "next_followup_date", label: "Next follow-up date", group: "Lead", resolve: (e, ctx) => fmtDate(e.nextFollowUpAt, ctx.timezone) },
  { value: "enquiry_address", label: "Lead address", group: "Lead", resolve: (e) => e.address || "" },
  { value: "enquiry_degree", label: "Degree", group: "Lead", resolve: (e) => e.degree || e.otherDegree || "" },
  { value: "enquiry_passout_year", label: "Passout year", group: "Lead", resolve: (e) => e.passoutYear || "" },
  { value: "enquiry_experience", label: "Experience", group: "Lead", resolve: (e) => e.experience || "" },
  { value: "education_stream", label: "Education stream", group: "Lead", resolve: (e) => e.educationStream || "" },
  { value: "reference_name", label: "Reference name", group: "Lead", resolve: (e) => e.referenceName || "" },
  { value: "campaign_name", label: "Ad campaign name", group: "Lead", resolve: (e) => e.campaign?.name || "" },

  /* Course */
  { value: "course_name", label: "Course name", group: "Course", resolve: (e) => e.course?.name || e.otherCourse || "" },
  { value: "course_code", label: "Course code", group: "Course", resolve: (e) => e.course?.shortCode || "" },
  { value: "course_duration", label: "Course duration", group: "Course", resolve: (e) => e.course?.duration || "" },
  { value: "course_mode", label: "Course mode", group: "Course", resolve: (e) => e.course?.mode || "" },
  { value: "course_fees", label: "Course fees", group: "Course", resolve: (e, ctx) => fmtLooseMoney(e.course?.fees, ctx.currency) },
  { value: "course_start_date", label: "Course start date", group: "Course", resolve: (e, ctx) => fmtDate(e.course?.startDate, ctx.timezone) },
  { value: "course_category", label: "Course category", group: "Course", resolve: (e) => e.course?.category || "" },
  { value: "course_level", label: "Course level", group: "Course", resolve: (e) => e.course?.level || "" },
  { value: "next_batch", label: "Upcoming batch", group: "Course", resolve: (e) => e.course?.upcomingBatch || e.course?.currentBatch || "" },

  /* Branch */
  { value: "branch_name", label: "Branch name", group: "Branch", resolve: (e) => e.branchId?.name || "" },
  { value: "branch_address", label: "Branch address", group: "Branch", resolve: (e) => e.branchId?.address || "" },
  { value: "branch_city", label: "Branch city", group: "Branch", resolve: (e) => e.branchId?.city || "" },
  { value: "branch_phone", label: "Branch phone", group: "Branch", resolve: (e) => e.branchId?.phone || "" },
  { value: "branch_email", label: "Branch email", group: "Branch", resolve: (e) => e.branchId?.email || "" },

  /* Team */
  { value: "assigned_to", label: "Assigned counsellor", group: "Team", resolve: (e) => adminName(e.assignedTo) },
  { value: "assigned_to_mobile", label: "Counsellor's mobile", group: "Team", resolve: (e) => e.assignedTo?.mobile || e.assignedTo?.phone || "" },

  /* Institute — same for every recipient, so served from the preview context */
  { value: "institute_name", label: "Institute name", group: "Institute", resolve: (e, ctx) => ctx.instituteName || "" },
  { value: "brand_name", label: "Brand name", group: "Institute", resolve: (e, ctx) => ctx.brandName || "" },
  { value: "legal_name", label: "Legal / company name", group: "Institute", resolve: (e, ctx) => ctx.legalName || "" },
  { value: "institute_email", label: "Institute email", group: "Institute", resolve: (e, ctx) => ctx.instituteEmail || "" },
  { value: "institute_phone", label: "Institute phone", group: "Institute", resolve: (e, ctx) => ctx.institutePhone || "" },
  { value: "institute_address", label: "Institute address", group: "Institute", resolve: (e, ctx) => ctx.instituteAddress || "" },
  { value: "portal_url", label: "Login / portal link", group: "Institute", resolve: (e, ctx) => ctx.portalUrl || "" },

  /* Date & time */
  { value: "current_date", label: "Today's date", group: "Date & time", resolve: (e, ctx) => ctx.currentDate || "" },
  { value: "current_time", label: "Current time", group: "Date & time", resolve: (e, ctx) => ctx.currentTime || "" },
  { value: "current_day", label: "Current weekday", group: "Date & time", resolve: (e, ctx) => ctx.currentDay || "" },
  { value: "current_month", label: "Current month", group: "Date & time", resolve: (e, ctx) => ctx.currentMonth || "" },
  { value: "current_year", label: "Current year", group: "Date & time", resolve: (e, ctx) => ctx.currentYear || "" },
  { value: "tomorrow_date", label: "Tomorrow's date", group: "Date & time", resolve: (e, ctx) => ctx.tomorrowDate || "" },
];

/**
 * The catalog bucketed into optgroups, in ENQUIRY_VAR_GROUP_ORDER.
 * A ~45-entry flat <select> is unreadable; grouped it is scannable.
 */
export const ENQUIRY_VAR_GROUPS = ENQUIRY_VAR_GROUP_ORDER
  .map((group) => ({ group, vars: ENQUIRY_DYNAMIC_VARS.filter((v) => v.group === group) }))
  .filter((g) => g.vars.length);

const BY_VALUE = new Map(ENQUIRY_DYNAMIC_VARS.map((v) => [v.value, v]));

export const dynamicVarLabel = (value) => BY_VALUE.get(value)?.label || value;

/**
 * Resolve a dynamic variable against one enquiry, for preview.
 * Falls back to a bracketed label so an empty field reads as "nothing here yet"
 * rather than as a gap in the message.
 */
export function resolveDynamicVar(value, enquiry, ctx = {}) {
  const def = BY_VALUE.get(value);
  if (!def || !enquiry) return `[${dynamicVarLabel(value)}]`;
  return def.resolve(enquiry, ctx) || `[${def.label}]`;
}
